import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import PageTransition from "@/components/PageTransition";
import ImprovedHowItWorksSection from "@/components/landing/ImprovedHowItWorksSection";
import SolutionSection from "@/components/landing/SolutionSection";
import UseCasesSection from "@/components/landing/UseCasesSection";
import ProblemSection from "@/components/landing/ProblemSection";
import CTASection from "@/components/landing/CTASection";

const Index = () => {
  const { user } = useAuth();

  if (user) {
    return (
      <div className="min-h-screen bg-white pt-24 pb-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold mb-6">Welcome back!</h1>
          <p className="text-lg text-gray-600 mb-8">
            Continue managing your inventory and tracking expirations.
          </p>
          <div className="flex justify-center">
            <Link to="/dashboard">
              <Button size="lg" className="bg-expitrack-primary hover:bg-expitrack-primary/90">
                Go to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PageTransition className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-20 pb-16 lg:pt-32 lg:pb-24" id="hero">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col items-center text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 rounded-full bg-expitrack-primary/10 text-expitrack-primary text-sm font-medium mb-6">
                Intelligent Expiry Management
              </span>
            </motion.div>

            <motion.h1
              className="text-4xl md:text-6xl font-bold mb-6 max-w-4xl leading-tight md:leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span className="block">Stop waste before it happens.</span>
              <span className="text-expitrack-primary">Predict expirations</span> with AI.
            </motion.h1>

            <motion.p
              className="text-lg text-gray-600 mb-10 max-w-2xl"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              ExpiTrack uses predictive AI to forecast upcoming expirations, visualize inventory health,
              and help you take action before it's too late.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Link
                to="/auth"
                className="px-8 py-4 bg-expitrack-primary text-white rounded-full font-medium hover:bg-expitrack-primary/90 transition-all shadow-md hover:shadow-lg flex items-center justify-center"
              >
                Get Started <ChevronRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/pricing"
                className="px-8 py-4 bg-white text-expitrack-text rounded-full font-medium border border-gray-200 hover:bg-gray-50 transition-all shadow-sm hover:shadow flex items-center justify-center"
              >
                See Pricing
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <ProblemSection />

      {/* Solution Section */}
      <SolutionSection />

      {/* How It Works Section */}
      <ImprovedHowItWorksSection />

      {/* Benefits Section */}
      <section id="benefits" className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div>
              <span className="px-4 py-2 rounded-full bg-expitrack-primary/10 text-expitrack-primary text-sm font-medium">
                Why ExpiTrack
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-6">Save money and reduce waste</h2>
              <p className="text-lg text-gray-600 mb-8">
                ExpiTrack doesn't just track expirations — it helps you take action before it's too late,
                resulting in real savings and reduced environmental impact.
              </p>

              <ul className="space-y-6">
                {[
                  {
                    title: "30% Reduction in Waste",
                    description: "Users report cutting their waste by up to 30% within the first month."
                  },
                  {
                    title: "Smart Notifications",
                    description: "Get timely alerts when items are approaching expiration."
                  },
                  {
                    title: "Advanced Analytics",
                    description: "Gain insights into consumption patterns and optimize purchasing."
                  }
                ].map((benefit, index) => (
                  <motion.li
                    key={index}
                    className="flex items-start"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 * index }}
                    viewport={{ once: true }}
                  >
                    <div className="w-6 h-6 bg-expitrack-primary/10 rounded-full flex items-center justify-center mt-1 mr-4">
                      <svg className="w-3 h-3 text-expitrack-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{benefit.title}</h3>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </motion.li>
                ))}
              </ul>

              <motion.div
                className="mt-10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                viewport={{ once: true }}
              >
                <Link
                  to="/auth"
                  className="px-6 py-3 bg-expitrack-primary text-white rounded-full font-medium hover:bg-expitrack-primary/90 transition-all shadow-md hover:shadow-lg inline-flex items-center"
                >
                  Start Tracking Now <ChevronRight className="ml-2 w-5 h-5" />
                </Link>
              </motion.div>
            </div>

            <div className="relative">
              <motion.div
                className="rounded-2xl overflow-hidden shadow-xl relative z-10"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
              >
                <img
                  src="/uploads/dashboard.png"
                  alt="ExpiTrack Dashboard"
                  className="w-full rounded-2xl"
                />
              </motion.div>
              <div className="absolute top-10 -right-10 w-72 h-72 bg-expitrack-primary/10 rounded-full filter blur-3xl -z-10"></div>
              <div className="absolute -bottom-10 -left-10 w-72 h-72 bg-blue-500/10 rounded-full filter blur-3xl -z-10"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Use Cases Section */}
      <UseCasesSection />

      {/* CTA Section */}
      <CTASection />
    </PageTransition>
  );
};

export default Index;
